import { CheckIcon } from '@heroicons/react/20/solid';
import { useState } from 'react';

import TextInput from '@/components/Forms/TextInput';
import { ClientButton } from '@/components/UI/Button';

import UpdateContactsForm from './UpdateContactsForm';

/**
 * The primary email row. By default it shows the confirmed address read-only
 * with an "Edit" link and a "Verified" badge (cleaner than an always-open edit
 * form). Clicking Edit — or an in-flight verification (a pending change in
 * meta.email) — reveals the full UpdateContactsForm with its verify-code flow.
 */
export default function PrimaryEmailField({
  user,
  setUser,
}: {
  user: API.User;
  setUser: (newUser: API.User | null) => void;
}) {
  const [editing, setEditing] = useState(false);
  const email = user?.email || '';
  // A new address awaiting its confirmation code lives in meta.email; while one
  // is pending we keep the form open so the user can finish verifying.
  const pending = !!user?.meta?.email;

  if (editing || pending) {
    return (
      <UpdateContactsForm
        user={user}
        setUser={setUser}
        name="email"
        needsPassword
        secondary
        onCancelAdd={() => setEditing(false)}
        onSuccess={() => setEditing(false)}
      />
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-3">
        <TextInput
          readOnly
          value={email}
          aria-label="Primary email address"
          className="max-w-sm cursor-default bg-zinc-50 text-zinc-600 dark:bg-white/5 dark:text-zinc-300"
        />
        <ClientButton
          type="button"
          variant="link"
          size="baseline"
          onClick={() => setEditing(true)}
          className="p-0 font-semibold text-zinc-700 no-underline hover:text-zinc-900 hover:underline dark:text-zinc-200 dark:hover:text-white"
        >
          Edit
        </ClientButton>
      </div>
      {!!email && (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-medium text-emerald-700 text-xs dark:bg-emerald-500/10 dark:text-emerald-400">
          <CheckIcon className="size-3.5" aria-hidden="true" />
          Verified
        </span>
      )}
    </div>
  );
}
